import { createContext } from "react";

import type {
  LiveStatusDetail,
  SessionMessagesResponseMessagesInner,
} from "src/clients/agent-management";
import type { ChatAttachmentPayload } from "src/unifiedCopilot/chat/contentBlocks";
import type {
  ContextualEntity,
  ContextualEntityKey,
} from "src/unifiedCopilot/context/types";

/** A draft held in memory while the current run unwinds after a stop. */
export type QueuedDraft = {
  /** Local identity for rendering; never sent to the backend. */
  id: string;
  /** Editor markdown captured at submit time (mentions keep their refs). */
  markdown: string;
  attachments: ChatAttachmentPayload[];
};

/**
 * Frontend session lifecycle for the unified copilot chat.
 *
 * - idle: nothing in flight; the composer owns the turn
 * - restoring: a picked transcript is loading behind a skeleton
 * - running: the agent is working, untouched by a stop
 * - stopping: stop clicked and the run still unwinds, but the composer
 *   reopens ahead of the terminal status so the next turn can be typed
 * - queued: drafts written during `stopping` are armed to flush as one
 *   invocation at the terminal status; Stop now means "unqueue"
 */
export type UnifiedCopilotSessionState =
  | "idle"
  | "restoring"
  | "running"
  | "stopping"
  | "queued";

/** The session will not take new input: it is working or still loading. */
export const isSessionBusy = (state: UnifiedCopilotSessionState): boolean =>
  state === "running" || state === "restoring";

/** A stop click has something to take back — the run, or the queued turn. */
export const isSessionInterruptible = (
  state: UnifiedCopilotSessionState,
): boolean => state === "running" || state === "queued";

/** A turn is outstanding, so the transcript is still growing. */
export const isSessionInFlight = (state: UnifiedCopilotSessionState): boolean =>
  state === "running" || state === "stopping" || state === "queued";

export type UnifiedCopilotChatSession = {
  messages: SessionMessagesResponseMessagesInner[];
  /**
   * Where the conversation sits in its lifecycle. Read it through the
   * predicates above so multi-state conditions stay in this module.
   */
  sessionState: UnifiedCopilotSessionState;
  /**
   * True only when the current session is provably unused: nothing sent,
   * nothing queued, and its transcript read has settled empty. A session whose
   * messages are still loading, or whose read errored, is deliberately not
   * counted as unused — there may be a conversation there to leave.
   */
  isUnusedSession: boolean;
  /**
   * Drafts queued during the stop window; rendered as normal bubbles.
   * Non-empty while `sessionState` is "queued", and after an unqueue until
   * the next submission carries them along.
   */
  queuedDrafts: QueuedDraft[];
  /**
   * Automatic ambient context sent with the next turn: what the page
   * registered, minus what the user dismissed. Explicit @-mentions travel as
   * message content blocks instead and are not part of this list.
   */
  contextualEntities: ContextualEntity[];
  sessionId: string | null;
  /**
   * Live metadata the runner pushes onto the session row while a turn is
   * active — currently what a long-running tool call is waiting on. The API
   * drops expired entries, so a present value is always current.
   */
  liveStatusDetail: LiveStatusDetail | null;
  send: (message: string, attachments?: ChatAttachmentPayload[]) => void;
  stop: () => void;
  /**
   * Drops an automatic entity from the context. Transient by design: it lasts
   * until the composer remounts, the session is reset or another one is
   * resumed, so an accidental dismissal is recovered by reopening the copilot.
   */
  dismissContextualEntity: (key: ContextualEntityKey) => void;
  /** Undoes every dismissal; the page's registered context applies again. */
  restoreContextualEntities: () => void;
  resetSession: () => void;
  resumeSession: (sessionId: string) => void;
};

export const UnifiedCopilotChatSessionContext =
  createContext<UnifiedCopilotChatSession | null>(null);
